/**
 * @FIXME(shawk):
 * Until an upstream dependency issue in "eslint-plugin-react" is resolved,
 * disabling eslint rule "react/prop-types" is the recommended approach
 * to resolve false positives in eslint.
 *
 * see:
 * - https://github.com/yannickcr/eslint-plugin-react/issues/3140
 * - https://github.com/redwoodjs/redwood/pull/3762
 */

import cx from 'classnames';
import React from 'react';
import { TFunction } from 'i18next';
import { FieldLabel, HelperText } from 'components/Forms';
import type { Props as IconProps } from 'components/Icon';
import { Icon } from 'components/Icon';
import { ConnectedFormInputProps } from 'types/form-field';
import { getTranslatedSelectOptionLabel } from 'domains/language/options';

export type SelectOption = {
  label: string;
  value: string;
};

export type Props = {
  allowEmpty?: boolean;
  options: SelectOption[];
  icon?: IconProps['name'];
  tPrefixKey?: string;
  tFunc?: TFunction;
} & ConnectedFormInputProps<HTMLSelectElement>;

export const SelectInput = React.forwardRef<HTMLSelectElement, Props>(function SelectInputWithRef(
  {
    name,
    label,
    labelClassName,
    className,
    error,
    hint,
    allowEmpty = false,
    disabled = false,
    placeholder = 'Select an option',
    options,
    icon,
    setError,
    setValue,
    clearValue,
    tFunc,
    tPrefixKey,
    ...inputProps
  },
  ref?: React.ForwardedRef<HTMLSelectElement>,
): JSX.Element {
  return (
    <div className={className}>
      <FieldLabel id={name} className={labelClassName}>
        {label}
      </FieldLabel>

      <div
        className={cx(
          'relative border ring-inset rounded-sm min-h-[48px] sm:min-h-[56px] flex justify-end transition-all',
          {
            ' bg-white hover:bg-purple-50 hover:border-black-900 focus-within:ring-1 focus-within:border-black-900 focus-within:ring-black-900 border-black-300':
              !disabled,
            'text-gray-900': !disabled && !error,
            'bg-black-4% text-black-38% border-black-38%': disabled && !error,
            'border-error-900': !!error,
            'cursor-pointer': !disabled,
          },
        )}
      >
        <div className="flex relative grow">
          <select
            {...inputProps}
            ref={ref}
            id={name}
            name={name}
            disabled={disabled}
            aria-invalid={error ? 'true' : 'false'}
            className="inset-0 py-8 sm:py-12 px-16 text-sm bg-transparent w-full appearance-none outline-none invalid:text-black-600"
          >
            {allowEmpty && <option value="">{placeholder}</option>}
            {options.map(option => {
              const { value: optionValue, label: optionLabel } = option;
              return (
                <option key={`${optionLabel}-${optionValue}`} value={optionValue}>
                  {getTranslatedSelectOptionLabel(option, tFunc, tPrefixKey)}
                </option>
              );
            })}
          </select>

          <div className="flex absolute top-0 right-0 bottom-0 pointer-events-none">
            {error && <Icon name="Error" className="mr-8 sm:mr-12 my-auto flex-shrink-0 w-20 sm:w-24 text-error-900" />}
            <Icon name={icon || 'ChevronDown'} className="w-16 mr-12 sm:mr-16 flex-shrink-0 fill-black-100%" />
          </div>
        </div>
      </div>

      {hint && <HelperText>{hint}</HelperText>}
      {error && <HelperText error>{error}</HelperText>}
    </div>
  );
});
